import React, { useEffect, useState } from 'react';
import { FormRadioGroup, Row, Col } from '@snsw/react-component-library';
import PropTypes from 'prop-types';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';
import { IndividualHolderDetailFields } from './IndividualHolderDetailFields';

const IndividualHolderFields = React.forwardRef((props, ref) => {
  const { value: savedHolderDetails, onErrorChange = () => {} } = props;

  const amIRegistrationHolderOptions = [
    { value: 'yes', label: ItemRegistrationHolderPage.individualHolderUseMyDetailsLabel },
    {
      value: 'no',
      label: ItemRegistrationHolderPage.individualHolderUseDifferentDetailsLabel,
    },
  ];

  const getSavedSelectedIsApplicantRegoHolderOptionVal = (savedHolderDetails) => {
    if (savedHolderDetails?.amIRegistrationHolder === true) return 'yes';
    if (savedHolderDetails?.amIRegistrationHolder === false) return 'no';
  };

  const [selectedIsApplicantRegoHolderOptionVal, setSelectedIsApplicantRegoHolderOptionVal] = useState(
    getSavedSelectedIsApplicantRegoHolderOptionVal(savedHolderDetails),
  );
  const [amIRegistrationHolder, setIsApplicantRegoHolder] = useState(
    savedHolderDetails?.amIRegistrationHolder ?? undefined,
  );
  const [errors, setErrors] = useState({
    amIRegistrationHolder: false,
  });
  const [showErrors, setShowErrors] = useState(false);
  const [holderDetails, setHolderDetails] = useState(savedHolderDetails);
  const [parentErrorSummary, setParentErrorSummary] = useState([]);
  const [childErrorSummary, setChildErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'amIRegistrationHolder',
      fieldValue: selectedIsApplicantRegoHolderOptionVal,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => `${ItemRegistrationHolderPage.amIRegistrationHolderLabel} is required.`,
    },
  ];

  const componentRef = React.useRef();

  useEffect(() => {
    if (selectedIsApplicantRegoHolderOptionVal === 'yes') setIsApplicantRegoHolder(true);
    if (selectedIsApplicantRegoHolderOptionVal === 'no') setIsApplicantRegoHolder(false);
  }, [selectedIsApplicantRegoHolderOptionVal]);

  useEffect(() => {
    if (props.onChange) {
      props.onChange({ amIRegistrationHolder, ...holderDetails });
    }

    if (showErrors) validateAll();
  }, [amIRegistrationHolder, holderDetails]);

  useEffect(() => {
    onErrorChange(parentErrorSummary.concat(childErrorSummary));
  }, [parentErrorSummary, childErrorSummary]);

  const validateAll = () => {
    let isCurrentComponentValid = validateExt(validators, setErrors, setParentErrorSummary);
    let isDetailsComponentValid = true;

    if (componentRef.current) isDetailsComponentValid = componentRef.current.validate();

    setShowErrors(!isCurrentComponentValid);

    return isCurrentComponentValid && isDetailsComponentValid;
  };

  if (ref) {
    ref.current = {
      validate: () => validateAll(),
    };
  }

  return (
    <>
      <Row style={{ marginTop: 20 }}>
        <Col span={6}>
          <FormRadioGroup
            id="amIRegistrationHolder"
            name="amIRegistrationHolder"
            legend={ItemRegistrationHolderPage.amIRegistrationHolderLabel}
            vertical={true}
            options={amIRegistrationHolderOptions}
            onChange={(v) => setSelectedIsApplicantRegoHolderOptionVal(v)}
            value={selectedIsApplicantRegoHolderOptionVal}
            errorMessage={errors?.amIRegistrationHolder?.errorMessage}
            hasError={showErrors && errors?.amIRegistrationHolder?.invalid}
          />
        </Col>
      </Row>

      {selectedIsApplicantRegoHolderOptionVal && (
        <IndividualHolderDetailFields
          ref={componentRef}
          onChange={setHolderDetails}
          value={savedHolderDetails}
          onErrorChange={setChildErrorSummary}
          applicantEmail={props.applicantEmail}
          amIRegistrationHolder={amIRegistrationHolder}
        />
      )}
    </>
  );
});

IndividualHolderFields.displayName = 'IndividualHolderFields';
IndividualHolderFields.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
  applicantEmail: PropTypes.string,
};

IndividualHolderFields.defaultProps = {
  onChange: null,
  value: null,
};

export { IndividualHolderFields };
